/* eslint-disable max-lines */
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import moment from 'moment-timezone';
import { firstValueFrom, take } from 'rxjs';

import {
  collapse,
  CollapseState,
  filterBoolean,
  noWhitespaceValidator,
  phoneNumberValidatorFn,
  toggleCollapseState,
} from '@core/common-legacy';
import { AuthenticationDialogComponent } from '@core/components-authorization-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { SeatKind, TellentDevFlag } from '@core/tellent-types';
import {
  getDefaultTimezone,
  getFormattedTimezone,
  TimeFormat,
  WeekStartDay,
} from '@core/time-legacy';
import {
  FormErrorDirective,
  SelectOption,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogRef,
  DialogService,
  DialogWidth,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  PhoneInputComponent,
  ProtectedControlDirective,
  SelectComponent,
} from '@core/user-interface-legacy';

import { ConfirmLoginChangeDialogComponent } from './confirm-login-change-dialog/confirm-login-change-dialog.component';
import { AvatarInputComponent } from '../../avatar-input/avatar-input.component';

export interface TellentUserProfileForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  login: FormControl<string>; // username
  phone: FormControl<string>;
  language: FormControl<string>; // tellent app language
  recruiteeLanguage: FormControl<string>;
  kiwiLanguage: FormControl<string>;
  timezone: FormControl<string>;
  timeFormat: FormControl<string>;
  weekStartDay: FormControl<string>;
}

/*
 eslint-disable
  @typescript-eslint/consistent-type-assertions,
 @angular-eslint/no-inputs-metadata-property,
*/

@Component({
  selector: 'tlc-current-user-profile',
  templateUrl: './current-user-profile.component.html',
  styleUrl: './current-user-profile.component.less',
  imports: [
    FormFieldComponent,
    InputComponent,
    PhoneInputComponent,
    IconComponent,
    SelectComponent,
    LabelComponent,
    ButtonComponent,
    ReactiveFormsModule,
    TranslatePipe,
    AvatarInputComponent,
    ButtonLoadingDirective,
    ProtectedControlDirective,
    FormErrorDirective,
  ],
  animations: [collapse],
  inputs: CurrentUserProfileComponent.NgInputs,
})
export class CurrentUserProfileComponent {
  public static NgInputs = ['memberGuid' as const];

  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly i18n = inject(I18nService);
  private readonly dialogRef = inject(DialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);

  protected readonly CollapseState = CollapseState;

  public readonly timeFormatOptions: Array<SelectOption<TimeFormat>> = [
    {
      label: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.user_profile_form.time_format.24_hour',
      ),
      value: TimeFormat.Hour24,
    },
    {
      label: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.user_profile_form.time_format.12_hour',
      ),
      value: TimeFormat.Hour12,
    },
  ];
  public readonly weekStartDayOptions: Array<SelectOption<WeekStartDay>> = [
    {
      label: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.user_profile_form.week_start_day.monday',
      ),
      value: WeekStartDay.Monday,
    },
    {
      label: this.i18n.translate(
        'tlc.current_user.dialog_2fa_sms.user_profile_form.week_start_day.sunday',
      ),
      value: WeekStartDay.Sunday,
    },
  ];

  public readonly phoneErrors: { [key: string]: string } = {
    invalidNumber: this.i18n.translate('tlc.common.errors.phone.invalid'),
    required: this.i18n.translate('tlc.common.errors.phone.required'),
    maxlength: this.i18n.translate('tlc.common.errors.phone.length'),
  };

  public memberGuid: string;

  public countryCode: string;
  public loading = signal<boolean>(false);
  public emailErrorMessage$ = signal<string>('');
  public phoneErrorMessages$ = signal<string[]>([]);

  public collapsedState = signal(CollapseState.Collapsed);

  public currentUser$ = this.currentUserFacade.currentUser$;

  public isAnyOrgOwner$ = this.currentUserFacade.isAnyOrgOwner$;

  public isRecruiteeAvailable = this.currentUserFacade.seatByKind(SeatKind.Recruitment);

  public isKiwiAvailable = this.currentUserFacade.seatByKind(SeatKind.CoreHR);

  public hasSeats = computed(() => this.isRecruiteeAvailable() || this.isKiwiAvailable());

  public hasWorkEmail = computed(() => {
    if (!this.usersWithoutEmails()) {
      return true; // Legacy behavior - always show email field
    }
    return !!this.currentUserFacade.currentUser()?.hasWorkEmail;
  });

  public tellentAndCoreSupportedLanguagesOptions = [
    { value: 'en', label: this.i18n.translate('tlc.common.languages.english') },
    { value: 'nl', label: this.i18n.translate('tlc.common.languages.dutch') },
    { value: 'de', label: this.i18n.translate('tlc.common.languages.german') },
    { value: 'fr', label: this.i18n.translate('tlc.common.languages.french') },
  ];

  public recruitmentSupportedLanguagesOptions = [
    { value: 'auto', label: this.i18n.translate('tlc.common.languages.auto') },
    { value: 'en', label: this.i18n.translate('tlc.common.languages.english') },
    { value: 'nl', label: this.i18n.translate('tlc.common.languages.dutch') },
    { value: 'de', label: this.i18n.translate('tlc.common.languages.german') },
    { value: 'fr', label: this.i18n.translate('tlc.common.languages.french') },
    { value: 'sv', label: this.i18n.translate('tlc.common.languages.swedish') },
    { value: 'fi', label: this.i18n.translate('tlc.common.languages.finnish') },
  ];

  public timeZoneOptions = moment.tz.names().map(timezone => ({
    label: getFormattedTimezone(timezone),
    value: timezone,
  }));

  public form = this.formBuilder.nonNullable.group<TellentUserProfileForm>({
    firstName: new FormControl<string>('', {
      nonNullable: true,
      validators: [noWhitespaceValidator, Validators.required],
    }),
    lastName: new FormControl<string>('', {
      nonNullable: true,
      validators: [noWhitespaceValidator, Validators.required],
    }),
    email: new FormControl<string>('', {
      validators: [Validators.email, Validators.required, noWhitespaceValidator],
      nonNullable: true,
    }),
    login: new FormControl<string>('', {
      nonNullable: true,
      validators: [noWhitespaceValidator, Validators.required],
    }),
    phone: new FormControl<string>('', {
      nonNullable: true,
      validators: [noWhitespaceValidator, Validators.required, phoneNumberValidatorFn],
    }),
    language: new FormControl<string>('en', { nonNullable: true }), // tellent app language
    recruiteeLanguage: new FormControl<string>('en', { nonNullable: true }),
    kiwiLanguage: new FormControl<string>('en', { nonNullable: true }),
    timezone: new FormControl<string>('', { nonNullable: true }),
    timeFormat: new FormControl<string>('24', { nonNullable: true }),
    weekStartDay: new FormControl<string>('monday', { nonNullable: true }),
  });

  public avatarFile: File | null;

  protected usersWithoutEmails = injectDevFlagSignal(TellentDevFlag.UsersWithoutEmails);

  public ngOnInit(): void {
    this.setupValidators();
    this.trackTellentLanguageChanges();
    this.fillFormWithData();
    this.resolvePhoneNumberValidator();
  }

  public toggle(): void {
    this.collapsedState.set(toggleCollapseState(this.collapsedState()));
    this.cdRef.detectChanges();
  }

  public cacheAvatarImg(file: File | null): void {
    this.avatarFile = file;
    this.setFormToDirty();
  }

  public async submit() {
    this.loading.set(true);

    const loginControl = this.form.controls.login;
    const loginChanged =
      loginControl.dirty && loginControl.value !== this.currentUserFacade.currentUser()?.login;

    if (loginChanged) {
      const { afterClosed } = this.dialogService.openCustom(
        ConfirmLoginChangeDialogComponent,
        {
          width: DialogWidth.SmallX,
          containerOptions: { fullscreenOnMobile: true },
        },
        { login: loginControl.value },
        this.vcRef,
      );
      const result = await firstValueFrom(afterClosed);

      if (!result) {
        this.loading.set(false);
        return;
      }
    }

    if (this.form.controls.email.dirty) {
      const { afterClosed } = this.dialogService.openCustom(
        AuthenticationDialogComponent,
        {
          width: DialogWidth.SmallX,
          containerOptions: { fullscreenOnMobile: true },
        },
        {},
        this.vcRef,
      );
      const emailCode = await firstValueFrom(afterClosed);

      if (emailCode) {
        this.dispatchFormUpdate(this.form, emailCode, this.avatarFile);
      }

      this.loading.set(false);
      return;
    }

    this.dispatchFormUpdate(this.form, undefined, this.avatarFile);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  private dispatchFormUpdate(
    form: FormGroup<TellentUserProfileForm>,
    code?: string,
    avatar?: File | null,
  ): void {
    const loginValue =
      this.usersWithoutEmails() && !this.hasWorkEmail() ? form.value.login : undefined;

    this.currentUserFacade.updateUser({
      ...form.value,
      code,
      avatar: avatar ? avatar : undefined,
      email: form.controls.email.dirty ? form.value.email : undefined,
      login: loginValue,
    });

    this.currentUserFacade.updateUserSuccess$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(action => {
        this.form.markAsPristine();
        this.loading.set(false);
        this.dialogRef.close({ success: true, payload: action.payload });
      });

    this.currentUserFacade.updateUserFail$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(error => {
        const emailError = error.payload.error.errors.email[0];
        const emailErrorMessage = this.i18n.translate(
          'tlc.current_user.dialog_2fa_sms.user_profile_form.data.email_already_taken_error',
        );
        if (emailError === 'has already been taken') this.emailErrorMessage$.set(emailErrorMessage);
        this.loading.set(false);
      });
  }

  private trackTellentLanguageChanges(): void {
    this.form.controls.language.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(language => {
        const { kiwiLanguage, recruiteeLanguage } = this.form.controls;
        kiwiLanguage.setValue(language, { emitEvent: false });
        recruiteeLanguage.setValue(language, { emitEvent: false });
      });
  }

  private setFormToDirty() {
    this.form.markAsDirty();
  }

  private fillFormWithData(): void {
    this.currentUser$
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(user => {
        const {
          firstName,
          lastName,
          email,
          phone,
          language,
          locale,
          login,
          timezone,
          timeFormat,
          appLanguages,
          weekStartDay,
        } = user;

        this.form.patchValue(
          {
            firstName,
            lastName,
            email,
            phone,
            language: language || locale, // tellent app language
            login,
            timeFormat,
            timezone: timezone || getDefaultTimezone(),
            recruiteeLanguage: appLanguages?.recruitment || language,
            kiwiLanguage: appLanguages?.coreHr || language,
            weekStartDay,
          },
          { emitEvent: true },
        );
        this.form.updateValueAndValidity();
      });
  }

  // Check whether the user is an owner of any org. If they are just a regular user, phone input is not required.
  private resolvePhoneNumberValidator(): void {
    this.isAnyOrgOwner$
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(isAnyOrgOwner => {
        if (!isAnyOrgOwner) {
          this.form.get('phone')?.setValidators([noWhitespaceValidator, phoneNumberValidatorFn]);
          this.form.updateValueAndValidity();
        }
      });
  }

  private setupValidators(): void {
    if (!this.usersWithoutEmails()) {
      // Legacy behavior - email is always required
      this.form.controls.email.setValidators([
        Validators.email,
        Validators.required,
        noWhitespaceValidator,
      ]);
      this.form.controls.login.clearValidators();
      this.form.controls.email.updateValueAndValidity();
      this.form.controls.login.updateValueAndValidity();
      return;
    }

    const emailControl = this.form.controls.email;
    const loginControl = this.form.controls.login;

    if (this.hasWorkEmail()) {
      emailControl.setValidators([Validators.email, Validators.required, noWhitespaceValidator]);
      loginControl.clearValidators();
    } else {
      loginControl.setValidators([noWhitespaceValidator, Validators.required]);
      emailControl.clearValidators();
    }

    emailControl.updateValueAndValidity();
    loginControl.updateValueAndValidity();
  }
}
